"use client";

import React, { useEffect, useRef, useState } from "react";
import { getApiUrl } from "@/lib/api";

type FormStatus = "idle" | "submitting" | "success" | "error";

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"] as const;

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.tabIndex !== -1
  );
}

type BookDemoModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function BookDemoModal({ open, onClose }: BookDemoModalProps) {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement;

    const dialog = dialogRef.current;
    if (dialog) {
      const focusTarget =
        dialog.querySelector<HTMLElement>("[aria-label='Close']") ??
        getFocusableElements(dialog)[0];
      focusTarget?.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialog) return;

      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);

      const previous = previousFocusRef.current;
      if (previous instanceof HTMLElement && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setStatus("idle");
      setErrorMessage(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: String(formData.get("name") ?? "").trim(),
      email: String(formData.get("email") ?? "").trim(),
      company: String(formData.get("company") ?? "").trim(),
      companySize: String(formData.get("companySize") ?? "").trim(),
      preferredTime: String(formData.get("preferredTime") ?? "").trim(),
      message: String(formData.get("message") ?? "").trim(),
    };

    try {
      const response = await fetch(`${getApiUrl()}/public/demo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(data?.message ?? "Unable to send request. Please try again.");
      }

      form.reset();
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to send request. Please try again."
      );
    }
  };

  const inputClassName =
    "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-zinc-500 focus:border-copper-500/50 focus:outline-none focus:ring-1 focus:ring-copper-500/50 transition-all disabled:opacity-70";

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center p-0 sm:p-4">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close dialog backdrop"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-demo-title"
        className="relative z-[61] w-full max-w-lg max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#141210] shadow-2xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-white/10 bg-[#141210]/95 px-5 py-4 backdrop-blur">
          <h2 id="book-demo-title" className="font-display text-lg font-bold text-white">
            Book a demo
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-zinc-300 hover:text-white"
            aria-label="Close"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-5">
          {status === "success" ? (
            <div className="text-center py-8">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-copper-900/30 text-copper-300 mb-6">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-display text-2xl font-bold text-white mb-2">Request received</h3>
              <p className="text-zinc-400">
                Thanks — check your email. We&apos;ll be in touch soon.
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-8 text-sm font-medium text-copper-300 hover:text-copper-200 transition-colors"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {status === "error" && errorMessage && (
                <div className="rounded-xl border border-red-500/30 bg-red-950/30 px-4 py-3 text-sm text-red-200">
                  {errorMessage}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="demo-name" className="text-sm font-medium text-zinc-300">
                    Name
                  </label>
                  <input
                    id="demo-name"
                    name="name"
                    required
                    maxLength={200}
                    disabled={status === "submitting"}
                    className={inputClassName}
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="demo-email" className="text-sm font-medium text-zinc-300">
                    Work email
                  </label>
                  <input
                    id="demo-email"
                    name="email"
                    type="email"
                    required
                    maxLength={320}
                    disabled={status === "submitting"}
                    className={inputClassName}
                    placeholder="jane@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="demo-company" className="text-sm font-medium text-zinc-300">
                    Company
                  </label>
                  <input
                    id="demo-company"
                    name="company"
                    required
                    maxLength={200}
                    disabled={status === "submitting"}
                    className={inputClassName}
                    placeholder="Acme Inc."
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="demo-company-size" className="text-sm font-medium text-zinc-300">
                    Company size
                  </label>
                  <select
                    id="demo-company-size"
                    name="companySize"
                    required
                    disabled={status === "submitting"}
                    defaultValue=""
                    className={inputClassName}
                  >
                    <option value="" disabled>
                      Select size
                    </option>
                    {COMPANY_SIZES.map((size) => (
                      <option key={size} value={size} className="bg-[#141210]">
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="demo-preferred-time" className="text-sm font-medium text-zinc-300">
                  Preferred time
                </label>
                <input
                  id="demo-preferred-time"
                  name="preferredTime"
                  required
                  maxLength={500}
                  disabled={status === "submitting"}
                  className={inputClassName}
                  placeholder="Tue/Thu afternoons PT"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="demo-message" className="text-sm font-medium text-zinc-300">
                  Message <span className="text-zinc-500 font-normal">(optional)</span>
                </label>
                <textarea
                  id="demo-message"
                  name="message"
                  rows={4}
                  maxLength={5000}
                  disabled={status === "submitting"}
                  className={`${inputClassName} resize-none`}
                  placeholder="Anything we should know before the demo?"
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-1 w-full rounded-xl border border-copper-300/30 bg-gradient-to-b from-copper-400 to-copper-600 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(184,115,51,0.25)] transition-all hover:from-copper-300 hover:to-copper-500 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === "submitting" ? "Sending..." : "Request demo"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
